/**
 * Custom report templates list. Free tier (custom_reports flag off) sees the
 * locked preview already shipped on /reports. Paid tier sees their templates.
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft, FileText, Plus, Loader2, Lock, Sparkles, ChevronRight,
  MoreVertical, Pencil, Copy, Trash2, Send, Search, SlidersHorizontal,
  ArrowUpDown, Users,
} from "lucide-react";
import { AssignTemplateDialog } from "@/components/reports/AssignTemplateDialog";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/auth";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { UpgradePrompt } from "@/components/UpgradePrompt";
import {
  listTemplates, createTemplate, deleteTemplate, duplicateTemplate, updateTemplate,
  type ReportTemplate, type TemplateStatus,
} from "@/lib/report-templates";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/reports/templates/")({
  component: TemplatesIndex,
  head: () => ({
    meta: [
      { title: "Custom report templates — MissionReady" },
      { name: "description", content: "Build and manage custom report templates with drag-and-drop fields." },
    ],
  }),
});

const EDIT_ROLES = ["org_owner", "admin", "supervisor"];

function TemplatesIndex() {
  const { currentOrg, currentRole, user } = useAuth();
  const flags = useFeatureFlags();
  const unlocked = flags.loaded && flags.custom_reports === true;
  const canEdit = EDIT_ROLES.includes(currentRole ?? "");
  const navigate = useNavigate();

  const [items, setItems] = useState<ReportTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);

  // Search, filter, sort state
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<TemplateStatus | "all">("all");
  const [sortBy, setSortBy] = useState<"updated" | "name-asc" | "name-desc" | "status">("updated");

  const filteredItems = useMemo(() => {
    let result = [...items];
    const q = search.trim().toLowerCase();
    if (q) {
      result = result.filter((t) =>
        t.name.toLowerCase().includes(q) ||
        (t.description ?? "").toLowerCase().includes(q)
      );
    }
    if (statusFilter !== "all") {
      result = result.filter((t) => t.status === statusFilter);
    }
    switch (sortBy) {
      case "name-asc":
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "name-desc":
        result.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case "status":
        result.sort((a, b) => a.status.localeCompare(b.status) || b.updated_at.localeCompare(a.updated_at));
        break;
      case "updated":
      default:
        result.sort((a, b) => b.updated_at.localeCompare(a.updated_at));
        break;
    }
    return result;
  }, [items, search, statusFilter, sortBy]);

  const reload = async () => {
    if (!currentOrg) return;
    setLoading(true);
    try {
      setItems(await listTemplates(currentOrg.id));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [currentOrg?.id]);

  const handleNew = () => {
    if (!unlocked) { setUpgradeOpen(true); return; }
    if (!canEdit) { toast.error("Supervisors and admins can create templates."); return; }
    setNewOpen(true);
  };

  const handleCreate = async (name: string, description: string) => {
    if (!currentOrg || !user) return;
    try {
      const t = await createTemplate({
        organization_id: currentOrg.id,
        name: name.trim() || "Untitled template",
        description: description.trim() || null,
        created_by: user.id,
      });
      setNewOpen(false);
      navigate({ to: "/reports/templates/$id", params: { id: t.id } });
    } catch (e) {
      toast.error(`Could not create template: ${String((e as Error).message ?? e)}`);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState<ReportTemplate | null>(null);
  const [renameTarget, setRenameTarget] = useState<ReportTemplate | null>(null);
  const [assignTarget, setAssignTarget] = useState<ReportTemplate | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    setBusyId(id);
    try {
      await deleteTemplate(id);
      setItems((xs) => xs.filter((x) => x.id !== id));
      toast.success("Template deleted");
    } catch (e) {
      toast.error(`Could not delete: ${String((e as Error).message ?? e)}`);
    } finally {
      setBusyId(null);
      setDeleteTarget(null);
    }
  };

  const handleDuplicate = async (tmpl: ReportTemplate) => {
    if (!user) return;
    setBusyId(tmpl.id);
    try {
      const copy = await duplicateTemplate(tmpl.id, { created_by: user.id });
      setItems((xs) => [copy, ...xs]);
      toast.success(`Duplicated as "${copy.name}"`);
    } catch (e) {
      toast.error(`Could not duplicate: ${String((e as Error).message ?? e)}`);
    } finally {
      setBusyId(null);
    }
  };

  const handleRename = async (newName: string) => {
    if (!renameTarget) return;
    const name = newName.trim();
    if (!name) return;
    const id = renameTarget.id;
    setBusyId(id);
    try {
      await updateTemplate(id, { name });
      setItems((xs) => xs.map((x) => (x.id === id ? { ...x, name } : x)));
      toast.success("Renamed");
    } catch (e) {
      toast.error(`Could not rename: ${String((e as Error).message ?? e)}`);
    } finally {
      setBusyId(null);
      setRenameTarget(null);
    }
  };


  return (
    <div className="mx-auto max-w-3xl px-4 pb-28 pt-6 md:px-8 md:py-8">
      <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2">
        <Link to="/reports"><ArrowLeft className="mr-1 h-4 w-4" /> All reports</Link>
      </Button>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight md:text-3xl">
            Custom report templates
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Build your own report forms with drag-and-drop fields.
          </p>
        </div>
        <Button onClick={handleNew} className="gap-2">
          {unlocked ? <Plus className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
          New template
        </Button>
      </div>

      {!unlocked && (
        <div className="mt-5 rounded-2xl border border-primary/30 bg-card p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-display text-base font-semibold">Unlock Custom Reports</h3>
              <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                Custom report templates with conditional fields, signatures, and photo uploads are available on every paid plan, starting with Solo Contractor.
              </p>
              <Button size="sm" className="mt-3 gap-1" onClick={() => setUpgradeOpen(true)}>
                Upgrade <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 rounded-2xl border border-border bg-card">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading templates…
          </div>
        ) : items.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <FileText className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="mt-3 text-sm font-medium">No templates yet</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {unlocked ? "Create your first template to get started." : "Upgrade to a paid plan to build templates."}
            </p>
          </div>
        ) : (
          <>
            {/* Toolbar */}
            <div className="flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search templates…"
                  className="h-9 pl-9"
                />
              </div>
              <div className="flex items-center gap-2">
                <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as TemplateStatus | "all")}>
                  <SelectTrigger aria-label="Status" className="h-9 w-[9rem] gap-1">
                    <SlidersHorizontal className="h-3.5 w-3.5 text-muted-foreground" />
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
                  <SelectTrigger aria-label="Sort" className="h-9 w-[9.5rem] gap-1">
                    <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" />
                    <SelectValue placeholder="Sort" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="updated">Recently updated</SelectItem>
                    <SelectItem value="name-asc">Name A → Z</SelectItem>
                    <SelectItem value="name-desc">Name Z → A</SelectItem>
                    <SelectItem value="status">Status</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {filteredItems.length === 0 ? (
              <div className="px-6 py-10 text-center">
                <p className="text-sm text-muted-foreground">No templates match your search.</p>
                <Button variant="link" size="sm" className="mt-1 h-auto p-0" onClick={() => { setSearch(""); setStatusFilter("all"); }}>
                  Clear filters
                </Button>
              </div>
            ) : (
              <>
                <div className="px-4 py-2 text-xs text-muted-foreground">
                  {filteredItems.length} template{filteredItems.length !== 1 ? "s" : ""}
                </div>
                <ul className="divide-y divide-border">
                  {filteredItems.map((t) => (
                    <li key={t.id} className="flex items-center gap-3 px-4 py-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <FileText className="h-5 w-5" />
                      </div>
                      <Link
                        to="/reports/templates/$id" params={{ id: t.id }}
                        className="min-w-0 flex-1"
                      >
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium text-foreground">{t.name}</span>
                          <Badge
                            variant="outline"
                            className={cn(
                              "text-[10px] px-1.5 py-0 h-4 rounded",
                              t.status === "published" && "border-emerald-500/40 text-emerald-400 bg-emerald-500/10",
                              t.status === "draft" && "border-amber-500/40 text-amber-400 bg-amber-500/10",
                              t.status === "archived" && "border-muted-foreground/40 text-muted-foreground bg-muted",
                            )}
                          >
                            {t.status}
                          </Badge>
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          Updated {new Date(t.updated_at).toLocaleDateString()}
                        </div>
                      </Link>
                      {t.status === "published" && (
                        <Button
                          asChild size="sm" variant="outline"
                          className="hidden sm:inline-flex"
                        >
                          <Link to="/reports/templates/$id/fill" params={{ id: t.id }}>
                            <Send className="mr-1 h-3.5 w-3.5" /> File
                          </Link>
                        </Button>
                      )}
                      {t.status === "published" && canEdit && (
                        <Button
                          size="sm" variant="outline"
                          className="hidden md:inline-flex"
                          onClick={() => setAssignTarget(t)}
                        >
                          <Users className="mr-1 h-3.5 w-3.5" /> Assign
                        </Button>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            size="icon" variant="ghost" className="h-9 w-9"
                            aria-label="Template actions" disabled={busyId === t.id}
                          >
                            {busyId === t.id
                              ? <Loader2 className="h-4 w-4 animate-spin" />
                              : <MoreVertical className="h-4 w-4" />}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44">
                          <DropdownMenuItem asChild>
                            <Link to="/reports/templates/$id" params={{ id: t.id }}>
                              <Pencil className="mr-2 h-4 w-4" /> Edit
                            </Link>
                          </DropdownMenuItem>
                          {t.status === "published" && (
                            <DropdownMenuItem asChild>
                              <Link to="/reports/templates/$id/fill" params={{ id: t.id }}>
                                <Send className="mr-2 h-4 w-4" /> File report
                              </Link>
                            </DropdownMenuItem>
                          )}
                          {t.status === "published" && canEdit && (
                            <DropdownMenuItem onSelect={() => setAssignTarget(t)}>
                              <Users className="mr-2 h-4 w-4" /> Assign to sites
                            </DropdownMenuItem>
                          )}
                          {canEdit && (
                            <DropdownMenuItem onSelect={() => setRenameTarget(t)}>
                              <Pencil className="mr-2 h-4 w-4" /> Rename
                            </DropdownMenuItem>
                          )}
                          {canEdit && (
                            <DropdownMenuItem onSelect={() => handleDuplicate(t)}>
                              <Copy className="mr-2 h-4 w-4" /> Duplicate
                            </DropdownMenuItem>
                          )}
                          {canEdit && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onSelect={() => setDeleteTarget(t)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="mr-2 h-4 w-4" /> Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </div>

      <NewTemplateDialog open={newOpen} onOpenChange={setNewOpen} onSubmit={handleCreate} />

      <UpgradePrompt
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        title="Unlock Custom Reports"
        reason="Custom report templates with drag-and-drop fields, signatures, and photo uploads are available on every paid plan."
        returnTo="/reports/templates"
        suggestions={[
          { planKey: "solo", label: "Upgrade to Solo Contractor", highlight: "$9/mo · Unlock custom report templates", interval: "month" },
          { planKey: "starter", label: "Upgrade to Starter", highlight: "$49/mo · Unlimited sites + 15 seats", interval: "month" },
        ]}
      />

      <RenameDialog
        target={renameTarget}
        onCancel={() => setRenameTarget(null)}
        onSubmit={handleRename}
      />

      <AssignTemplateDialog
        template={assignTarget}
        onOpenChange={(v) => { if (!v) setAssignTarget(null); }}
      />


      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete template?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget ? (
                <>
                  This will permanently delete <strong className="text-foreground">{deleteTarget.name}</strong> and every submission filed against it. This action cannot be undone.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function NewTemplateDialog({
  open, onOpenChange, onSubmit,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSubmit: (name: string, description: string) => void | Promise<void>;
}) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) { setName(""); setDesc(""); setBusy(false); } }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New report template</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Template name</Label>
            <Input aria-label="Template name"
              value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Site Inspection" autoFocus className="mt-1"
            />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea aria-label="Description"
              rows={3} value={desc} onChange={(e) => setDesc(e.target.value)}
              placeholder="What this template is for (optional)" className="mt-1"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            disabled={busy || name.trim().length === 0}
            onClick={async () => { setBusy(true); await onSubmit(name, desc); setBusy(false); }}
          >
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RenameDialog({
  target, onCancel, onSubmit,
}: {
  target: ReportTemplate | null;
  onCancel: () => void;
  onSubmit: (name: string) => void | Promise<void>;
}) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (target) { setName(target.name); setBusy(false); }
  }, [target]);

  return (
    <Dialog open={target !== null} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Rename template</DialogTitle>
        </DialogHeader>
        <div>
          <Label>Name</Label>
          <Input aria-label="Name"
            value={name} onChange={(e) => setName(e.target.value)}
            autoFocus className="mt-1"
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button
            disabled={busy || name.trim().length === 0 || name.trim() === target?.name}
            onClick={async () => { setBusy(true); await onSubmit(name); setBusy(false); }}
          >
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
